import React, { useEffect, useMemo, useRef } from "react";
import { useVisibilityChange } from "@uidotdev/usehooks";
import { Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { productActions, productAutomationActions, productClientsActions, RequestStatus } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { ProductChargeType } from "@fitness-app/data-models/entities/Product";

import { usePermission } from "~/hooks/usePermission";
import ProductAnalytics from "~/modules/Products/Product/ProductAnalytics/ProductAnalytics";
import ProductAutomation from "~/modules/Products/Product/ProductAutomation/ProductAutomation";
import ProductClientsList from "~/modules/Products/Product/ProductClientsList/ProductClientsList";
import ProductDetails from "~/modules/Products/Product/ProductDetails/ProductDetails";
import ProductOrders from "~/modules/Products/Product/ProductOrders/ProductOrders";
import ProductResources from "~/modules/Products/Product/ProductResources/ProductResources";
import ProductSales from "~/modules/Products/Product/ProductSales/ProductSales";
import ProductSettings from "~/modules/Products/Product/ProductSettings/ProductSettings";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { useProductDetails } from "../hooks/useProductDetails";
import { getAvailableProductTabs, getDefaultProductTab, resolveProductTab } from "./productTabs";

const Product = (): React.ReactElement | null => {
  const { t } = useTranslation(["products", "common"]);

  const { id, tab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const productDetailsStatus = useAppSelector((state) => state.product.productDetailsStatus);
  const productDetails = useAppSelector((state) => state.product.details);
  const canManageProducts = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  const hasProductResourcesReadGrant = usePermission(PermissionKey.PRODUCTS_RESOURCES_READ);
  const canWriteProductResources = usePermission(PermissionKey.PRODUCTS_RESOURCES_WRITE);
  const canReadProductResources = canManageProducts || hasProductResourcesReadGrant;
  useProductDetails(
    id ?? "",
    canManageProducts && !productDetails && productDetailsStatus === RequestStatus.SUBSCRIBED,
  );

  const tabFromUrl = tab || location.pathname.split("/").slice(-1)[0] || "";

  const isFree = useAppSelector((state) => state.product.details?.chargeType === ProductChargeType.FREE);

  const availableTabs = useMemo(
    () =>
      getAvailableProductTabs({
        canManageProducts,
        canReadResources: canReadProductResources,
        isFree,
      }),
    [canManageProducts, canReadProductResources, isFree],
  );
  const defaultTab = getDefaultProductTab(canManageProducts);
  const resolvedTab = resolveProductTab(tabFromUrl, availableTabs);
  const activeTab = resolvedTab ?? defaultTab;
  const dispatch = useAppDispatch();
  const documentVisible = useVisibilityChange();
  const initialLoad = useRef(true);

  useEffect(() => {
    if (canManageProducts && id && documentVisible && productDetailsStatus === null && !initialLoad.current) {
      dispatch(productActions.subscribeToProduct(id));
      dispatch(productAutomationActions.subscribeToProductAutomation(id));
    }

    initialLoad.current = false;
  }, [canManageProducts, documentVisible, productDetailsStatus, dispatch, id]);

  useEffect(() => {
    if (canManageProducts && id) {
      dispatch(productActions.subscribeToProduct(id));
      dispatch(productAutomationActions.subscribeToProductAutomation(id));
    }

    return () => {
      dispatch(productActions.unsubscribeFromProductDetails());
      dispatch(productAutomationActions.unsubscribeFromProductAutomation());
      dispatch(productClientsActions.clearProductClients());
    };
  }, [canManageProducts, id, dispatch]);

  useEffect(() => {
    if (tabFromUrl !== activeTab) {
      navigate(`/products/${id}/${activeTab}`, { replace: Boolean(resolvedTab) });
    }
  }, [activeTab, id, navigate, resolvedTab, tabFromUrl]);

  if (!id) {
    return null;
  }

  return (
    <div className="p-0 lg:p-6">
      <Tabs
        type="card"
        className="tabs-reset"
        activeKey={activeTab}
        onChange={(tab) => navigate(`/products/${id}/${tab}`)}
      >
        {canManageProducts ? (
          <>
            <Tabs.TabPane key="details" tab={t("productDetails.title")}>
              <ProductDetails />
            </Tabs.TabPane>

            {/* Prices, checkout variants and funnels used to render inside the details tab —
                all at once, all mounted. Kept behind their own tab and destroyed when it is
                left, so opening a product no longer pays for a few hundred price rows. */}
            {!isFree ? (
              <Tabs.TabPane key="sales" tab={t("salesTab")} destroyInactiveTabPane>
                <ProductSales />
              </Tabs.TabPane>
            ) : null}

            <Tabs.TabPane key="clients" tab={t("clientsList")}>
              <ProductClientsList productId={id} />
            </Tabs.TabPane>

            {!isFree ? (
              <Tabs.TabPane key="transactions" tab={t("orders.title")}>
                <ProductOrders productId={id} />
              </Tabs.TabPane>
            ) : null}

            {!isFree ? (
              <Tabs.TabPane key="analytics" tab={t("analyticsTab")} destroyInactiveTabPane>
                <ProductAnalytics productId={id} />
              </Tabs.TabPane>
            ) : null}

            <Tabs.TabPane key="automation" tab={t("productAutomation.title")}>
              <ProductAutomation productId={id} />
            </Tabs.TabPane>

            <Tabs.TabPane key="settings" tab={t("productAutomation.settings")} destroyInactiveTabPane>
              <ProductSettings productId={id} />
            </Tabs.TabPane>
          </>
        ) : null}

        {canReadProductResources ? (
          <Tabs.TabPane key="resources" tab={t("productResources.title")} destroyInactiveTabPane>
            <ProductResources productId={id} readonly={!canManageProducts && !canWriteProductResources} />
          </Tabs.TabPane>
        ) : null}
      </Tabs>
    </div>
  );
};

export default Product;
